import produce from 'immer';

import { createKeyInTransform, createKeyNotInTransform } from 'Lib/persist/transforms';
import type { Action } from 'Lib/reduxType';
import { omit } from 'Lib/utils/lodashUtils';
import uuid from 'Lib/utils/uuid';

import { DELETE_ONBOARDING_SUCCESS } from 'Store/authOnboarding/constants';
import { getCommonResetAction } from 'Store/commonActions';
import {
  AUTH_HEALTHY,
  AUTH_OUTAGE,
  CLEAR_LOGIN_ERROR,
  GUEST_LOGIN_START,
  GUEST_LOGIN_SUCCESS,
  LOGIN_FAILURE,
  LOGIN_START,
  LOGIN_SUCCESS,
  REAUTH,
  SET_APPEAL_TOKEN,
  SET_AUTH_FUNNEL_SESSION_ID,
  SET_AUTH_TOKEN,
  SET_AUTH_TOKEN_EXPIRATION,
  SET_CAPTCHA_KEY,
  SET_CAPTCHA_TYPE,
  SET_MAGIC_LINK_TYPE,
  SET_ONBOARDING_TOKEN,
  SET_ONBOARDING_USER_ID,
  SET_REFRESH_TOKEN,
  SET_SELFIE_CHALLENGE_TOKEN,
  SET_SELFIE_CHALLENGE_V2_STATUS,
  SET_SMS_CAPTCHA_TOKEN,
} from 'Store/mfa/constants';
import { SMS_CAPTCHA_COMPLETED } from 'Store/sms/constants';

import initialState from './initialState';
import type { MfaState } from './types';

const FUNNEL_SESSION_ID_FORMAT = 'xxxxxxxxxxxxxxxx';

export const reducer = produce<MfaState, [Action]>((draft, action) => {
  switch (action.type) {
    case LOGIN_START: {
      const { loginType } = action.payload;
      Object.assign(draft, omit(initialState, ['funnelSessionId']));
      draft.loginType = loginType;
      break;
    }

    case GUEST_LOGIN_START: {
      Object.assign(draft, omit(initialState, ['funnelSessionId']));
      draft.isGuestAuth = true;
      break;
    }

    case SET_AUTH_FUNNEL_SESSION_ID: {
      const funnelSessionId = uuid(FUNNEL_SESSION_ID_FORMAT);
      draft.funnelSessionId = funnelSessionId;
      break;
    }

    case SET_REFRESH_TOKEN: {
      const { refreshToken } = action.payload;
      draft.refreshToken = refreshToken;
      break;
    }

    case SET_CAPTCHA_KEY: {
      const { captchaKey } = action.payload;
      draft.captchaKey = captchaKey;
      break;
    }

    case SET_MAGIC_LINK_TYPE: {
      const magicLinkType = action.payload;
      draft.magicLinkType = magicLinkType;
      break;
    }

    case SET_ONBOARDING_TOKEN: {
      const { onboardingToken } = action.payload;
      draft.onboardingToken = onboardingToken;
      break;
    }

    case SET_ONBOARDING_USER_ID: {
      const { onboardingUserId } = action.payload;
      draft.onboardingUserId = onboardingUserId;
      break;
    }

    case SET_AUTH_TOKEN: {
      const { authToken } = action.payload;
      draft.authToken = authToken;
      draft.isOutage = false;
      break;
    }

    case SET_APPEAL_TOKEN: {
      const { appealToken } = action.payload;
      draft.appealToken = appealToken;
      break;
    }

    case SET_SELFIE_CHALLENGE_TOKEN: {
      const { selfieChallengeToken } = action.payload;
      draft.selfieChallengeToken = selfieChallengeToken;
      break;
    }

    case SET_SELFIE_CHALLENGE_V2_STATUS: {
      const { selfieStatus } = action.payload;
      draft.selfieStatus = selfieStatus;
      break;
    }

    case SET_AUTH_TOKEN_EXPIRATION: {
      const { authTokenExpiration } = action.payload;
      draft.authTokenExpiration = authTokenExpiration;
      break;
    }

    case SMS_CAPTCHA_COMPLETED:
    case SET_SMS_CAPTCHA_TOKEN: {
      const { smsCaptchaToken } = action.payload;
      draft.smsCaptchaToken = smsCaptchaToken;
      break;
    }

    case SET_CAPTCHA_TYPE: {
      const { captchaType } = action.payload;
      draft.captchaType = captchaType;
      break;
    }

    case AUTH_OUTAGE: {
      draft.isOutage = true;
      break;
    }

    case AUTH_HEALTHY: {
      draft.isOutage = false;
      break;
    }

    case REAUTH: {
      draft.isReauthing = true;
      break;
    }

    case GUEST_LOGIN_SUCCESS: {
      const { token } = action.payload;
      draft.guestAuthToken = token;
      break;
    }

    case LOGIN_SUCCESS: {
      draft.isReauthing = false;
      draft.guestAuthToken = '';
      draft.isGuestAuth = false;
      draft.onboardingUserId = null;
      break;
    }

    case LOGIN_FAILURE: {
      const { error } = action.payload;
      draft.isReauthing = false;
      draft.loginError = error;
      break;
    }

    case CLEAR_LOGIN_ERROR: {
      draft.loginError = null;
      break;
    }

    case DELETE_ONBOARDING_SUCCESS: {
      draft.onboardingToken = initialState.onboardingToken;
      draft.onboardingUserId = initialState.onboardingUserId;
      break;
    }

    case getCommonResetAction(action.type): {
      Object.assign(draft, omit(initialState, ['funnelSessionId']));
      break;
    }

    default:
      break;
  }
}, initialState);

export const persistConfig = {
  transforms: [
    createKeyInTransform([
      'authToken',
      'appealToken',
      'authTokenExpiration',
      'captchaType',
      'funnelSessionId',
      'guestAuthToken',
      'loginType',
      'onboardingUserId',
      'refreshToken',
    ]),
    createKeyNotInTransform(['selfieChallengeToken', 'selfieStatus']),
  ],
  wait: 100,
};
